import { useState } from 'react';

import { makeStyles } from '@material-ui/core/styles';
import Container from '@material-ui/core/Container';
import { useRouter } from 'next/router';

import Form from '@bequestinc/wui/layout/form';
import Button from '@bequestinc/wui/input/button';
import Spacer from '@bequestinc/wui/layout/spacer';
import Textbox from '@bequestinc/wui/input/textbox';
import Typography from '@bequestinc/wui/basics/typography';

import { useInputFieldState } from 'hooks';
import { resetPassword } from 'utils/API';
import { validateEmail } from 'utils/validation';
import Page from 'components/Page';
import Success from 'components/Success';

const useStyles = makeStyles(theme => ({
  root: {
    padding: [[32, 20]],
    [theme.breakpoints.notPhone]: {
      padding: [[32, 60]],
    },
  },
}));

const ResetPassword = () => {
  const [email, onChangeEmail] = useInputFieldState('');
  const [inputError, setInputError] = useState('');
  const [validationError, setValidationError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const classes = useStyles();
  const router = useRouter();
  const { next } = router.query;

  const validateForm = () => {
    let error = '';
    if (!email) {
      error = 'Please enter your email.';
    } else {
      error = validateEmail(email);
    }

    setInputError(error);

    return !error;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!validateForm()) {
      setSuccess(false);
      return;
    }
    try {
      setIsLoading(true);
      await resetPassword({
        email: email.toLowerCase(),
        redirectUrl: next,
      });
      setSuccess(true);
      setValidationError(null);
    } catch (error) {
      setValidationError(
        'Please enter the email associated with your MetLife Legal Plans account.',
      );
      setSuccess(false);
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => (
    <Page>
      <Container maxWidth="md" className={classes.root}>
        <Spacer v={32} />
        <Typography variant="h4" component="h1">
          Reset Password
        </Typography>

        <Form error={validationError} onSubmit={handleSubmit} noMargin noValidate>
          <Textbox
            name="email"
            type="email"
            label="Recovery Email"
            autoComplete="off"
            value={email}
            onChange={onChangeEmail}
            error={inputError}
          />
          <Spacer v={16} />
          <Button
            variant="contained"
            color="primary"
            type="submit"
            noMinWidth
            size="large"
            disabled={isLoading}
          >
            Request Reset
          </Button>
        </Form>
      </Container>
    </Page>
  );

  const successMessage = () => (
    <Page>
      <Container maxWidth="md" className={classes.root}>
        <Success header="Reset Password" message="Check your email for a password reset link." />
      </Container>
    </Page>
  );

  return success ? successMessage() : resetForm();
};

ResetPassword.public = true;

export default ResetPassword;
